import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { GraduationCap, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    try {
      await login(username.trim(), password);
      toast.success("Bienvenue !");
      navigate("/app");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Identifiants invalides";
      setErrorMsg(typeof msg === "string" ? msg : "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F8F9] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#1F7A63] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#E6B800]/20" />
        <div className="absolute -left-16 bottom-10 w-72 h-72 rounded-full bg-white/5" />
        <Link to="/" className="flex items-center gap-2 relative z-10" data-testid="login-back-home">
          <div className="w-10 h-10 rounded-lg bg-white grid place-items-center">
            <GraduationCap className="w-6 h-6 text-[#1F7A63]" />
          </div>
          <span className="font-bold text-xl" style={{ fontFamily: "Outfit" }}>EDUKATI</span>
        </Link>
        <div className="relative z-10">
          <h1 className="text-4xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            La gestion scolaire,<br /> au service du Sénégal.
          </h1>
          <p className="mt-4 text-white/80 max-w-md">
            Connectez-vous à votre espace pour suivre vos élèves, vos notes et vos bulletins.
          </p>
        </div>
        <div className="relative z-10 text-sm text-white/60">© {new Date().getFullYear()} EDUKATI V2</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center justify-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-[#1F7A63] grid place-items-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl" style={{ fontFamily: "Outfit" }}>EDUKATI</span>
          </div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Connexion</h2>
          <p className="mt-1 text-sm text-[#5C736A]">Accédez à votre tableau de bord</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="username">Identifiant</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ex : amardiallokdg"
                className="mt-1.5 h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                className="mt-1.5 h-12"
                required
              />
            </div>
            {errorMsg && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" data-testid="login-error">{errorMsg}</div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              className="w-full h-12 bg-[#1F7A63] hover:bg-[#165c4b] text-white text-base"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Se connecter
            </Button>
          </form>

          <div className="mt-8 text-sm text-[#5C736A] text-center">
            Pas encore de compte ?{" "}
            <Link to="/inscription" className="font-semibold text-[#1F7A63] hover:underline" data-testid="login-signup-link">Créer un compte</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
